import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { SharedModule, NotificationService } from '@vendure/admin-ui/core';
import { Query } from '../generated';
import { GetCollectionContentsQuery } from '@vendure/admin-ui/core';
import {
    DataService,
    CustomDetailComponent
} from '@vendure/admin-ui/core';
import { Observable, Subject, EMPTY } from 'rxjs';
import { switchMap, debounceTime, distinctUntilChanged, startWith, skip, takeUntil, map } from 'rxjs/operators';
import { ID, ProductVariant, Product } from '@vendure/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { safeJSONParse } from '../utils';
import {
    CollectionItemOrderData,
    CollectionItemType
} from '../ui-types';
import { GET_COLLECTION_ITEM_ORDER_DATA, PREVIEW_COLLECTION_ITEM_ORDER_DATA } from '../graphql';

@Component({
    selector: 'collection-variant-order-form',
    template: `
    <vdr-card class="pb-4">
        <!-- Top line with label -->
        <div class="header-row">
            <div>
                <p class="title">Product (Variant) Order</p>
                <p class="note-text">(*Better to order with less than 100 items)</p>
            </div>
        </div>

        <!-- Enable toggle button -->
        <div class="toggle-wrapper">
            <clr-toggle-wrapper>
                <input
                    type="checkbox"
                    clrToggle
                    name="enabled"
                    [formControl]="enabledControl"
                />
            </clr-toggle-wrapper>
        </div>

        <!-- Order Type Radio Buttons with Item Counts -->
        <div *ngIf="enabledControl.value" class="radio-group">
            <label>Order By:</label>
            <clr-radio-wrapper class="radio-item">
                <input
                    type="radio"
                    clrRadio
                    [formControl]="orderTypeControl"
                    value="Product"
                />
                <label>Product ({{ originalProducts?.length || 0 }} items)</label>
            </clr-radio-wrapper>
            <clr-radio-wrapper class="radio-item">
                <input
                    type="radio"
                    clrRadio
                    [formControl]="orderTypeControl"
                    value="ProductVariant"
                />
                <label>Product Variant ({{ originalVariants?.length || 0 }} items)</label>
            </clr-radio-wrapper>
        </div>

        <!-- Drop list for reordering items -->
        <div *ngIf="enabledControl.value && originalVariants?.length > 0">
            <div *ngIf="orderTypeControl.value === 'ProductVariant'">
                <div cdkDropList (cdkDropListDropped)="drop($event)">
                    <div *ngFor="let variant of orderedVariants; let i = index" cdkDrag class="item-row">
                        <p class="item-index">{{ i + 1 }}</p>
                        <img [src]="variant.featuredAsset?.preview + '?preset=tiny'" [alt]="variant.name" class="item-image">
                        <div class="item-details">
                            <div class="product-name">{{ variant.product?.name }}</div>
                            <div>{{ variant.name }}</div>
                            <div class="item-sku">{{ variant.sku }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div *ngIf="orderTypeControl.value === 'Product'">
                <div cdkDropList (cdkDropListDropped)="drop($event)">
                    <div *ngFor="let product of orderedProducts; let i = index" cdkDrag class="item-row">
                        <p class="item-index">{{ i + 1 }}</p>
                        <img [src]="product.featuredAsset?.preview + '?preset=tiny'" [alt]="product.name" class="item-image">
                        <div class="item-details">
                            <div class="product-name">{{ product?.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </vdr-card>
    `,
    styles: [`
        .header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }
        .title {
            font-size: 1.2rem;
            font-weight: 600;
        }
        .note-text {
            font-size: 0.6rem;
            color: #888;
        }
        .toggle-wrapper {
            margin-bottom: 1rem;
        }
        .radio-group {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
        }
        .radio-group label {
            margin-right: 1rem;
            font-weight: 500;
        }
        .radio-item {
            margin-right: 1.5rem;
        }
        .item-row {
            display: flex;
            align-items: center;
            padding: 0.5rem;
            border-bottom: 1px solid #ddd;
        }
        .item-index {
            font-weight: 600;
            margin-right: 0.5rem;
        }
        .item-image {
            width: 50px;
            height: auto;
            margin-right: 1rem;
        }
        .item-details {
            font-size: 0.875rem;
            line-height: 1.25rem;
        }
        .product-name {
            font-weight: 600;
        }
        .item-sku {
            color: #666;
        }
    `],
    standalone: true,
    imports: [SharedModule],
})
export class CollectionVariantOrderFormComponent implements CustomDetailComponent, AfterViewInit, OnDestroy {
    entity$: Observable<NonNullable<GetCollectionContentsQuery['collection']>>;
    detailForm: FormGroup;
    enabledControl = new FormControl(false);
    orderTypeControl = new FormControl(CollectionItemType.Product);

    private collectionId: ID | null = null;
    private controlsBound = false;
    private destroy$ = new Subject<void>();
    private itemDataRefresh$ = new Subject<boolean>();

    originalVariants: ProductVariant[] = [];
    orderedVariants: ProductVariant[] = [];
    variantMap = new Map<ID, ProductVariant>();
    originalProducts: Product[] = [];
    orderedProducts: Product[] = [];
    productMap = new Map<ID, Product>();
    collectionItemOrderData: CollectionItemOrderData;

    static readonly MAX_ITEMS = 150;

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private changeDetectorRef: ChangeDetectorRef,
        private hostElement: ElementRef<HTMLElement>,
    ) {}

    ngAfterViewInit() {
        // Hide the built-in "Custom Fields" card — itemOrderData is stored there
        // as a hidden input, so the section renders empty. Navigate from this
        // component's host up to vdr-collection-detail, find the card that wraps
        // vdr-tabbed-custom-fields, and remove it from view.
        const collectionDetail = this.hostElement.nativeElement.closest('vdr-collection-detail');
        const card = collectionDetail
            ?.querySelector('vdr-tabbed-custom-fields')
            ?.closest<HTMLElement>('vdr-card');
        if (card) {
            card.style.display = 'none';
        }
    }

    ngOnInit() {
        this.bindControlValueChanges();
        this.bindCollectionContentChanges();
        this.bindItemDataRefresh();

        this.entity$.pipe(
            switchMap((collection: any) => {
                this.collectionId = collection?.id ?? null;
                return this.dataService.query<Query['collection']>(GET_COLLECTION_ITEM_ORDER_DATA,
                    { id: collection?.id, maxVariants: CollectionVariantOrderFormComponent.MAX_ITEMS },
                    'network-only'
                ).mapSingle((data: any) => data?.collection);
            }),
            takeUntil(this.destroy$),
        ).subscribe((collection: any) => {
            if (collection) {
                this.hydrateItemsFromCollection(collection);
                // Only (re-)read collectionItemOrderData from the DB value on first
                // load or when navigating to a different collection. On every subsequent
                // entity$ emission, the in-memory value IS the source of truth — it is
                // exactly what was submitted to the server.
                const isFirstLoad = this.collectionItemOrderData == null;
                const isNewCollection = this.collectionItemOrderData?.collectionId !== collection.id;
                if (isFirstLoad || isNewCollection) {
                    const defaultData: CollectionItemOrderData = {
                        collectionId: null, enabled: false,
                        orderType: CollectionItemType.Product,
                        variantIdsInOrder: [], productIdsInOrder: [],
                    };
                    this.collectionItemOrderData = safeJSONParse(
                        collection.customFields?.itemOrderData,
                        this.notificationService,
                        defaultData
                    )!;
                    this.collectionItemOrderData!.collectionId = collection.id;
                    this.enabledControl.setValue(this.collectionItemOrderData?.enabled || false, { emitEvent: false });
                    this.orderTypeControl.setValue(this.collectionItemOrderData?.orderType || CollectionItemType.Product, { emitEvent: false });
                    this.syncOrderedItems();
                } else {
                    // Subsequent entity$ emissions (e.g. after Update): the in-memory
                    // orderedVariants IS the source of truth. Reconcile to add/remove
                    // items that changed, but never recompute order from IDs.
                    this.reconcileOrderedItems(false);
                }
                // Re-run preview to correct items in case Vendure's async filter job
                // hasn't updated the DB yet (don't mark form dirty — this is a refresh)
                this.fetchPreviewCollectionData(false);
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private fetchPreviewCollectionData(markDirty = true) {
        this.itemDataRefresh$.next(markDirty);
    }

    private bindItemDataRefresh() {
        this.itemDataRefresh$.pipe(
            takeUntil(this.destroy$),
            switchMap(markDirty => this.fetchLatestItems(markDirty)),
        ).subscribe(({ items, markDirty }) => {
            this.hydrateItemsFromVariants(items);
            this.reconcileOrderedItems(markDirty);
        });
    }

    private fetchLatestItems(markDirty: boolean): Observable<{ items: ProductVariant[]; markDirty: boolean }> {
        const filtersControl = this.detailForm?.get('filters');
        const inheritFiltersControl = this.detailForm?.get('inheritFilters');
        // When filters are cleared, fall back to the DB-persisted collection items so
        // stale preview data (from the previous filter state) is replaced and the order
        // is re-synced and written back to the form.
        // Guard with markDirty so passive refreshes (initial load, post-save entity$
        // re-emission) don't re-fetch DB items needlessly when no filters are active.
        if (!filtersControl?.value?.length) {
            if (markDirty && this.collectionId) {
                return this.dataService.query<Query['collection']>(GET_COLLECTION_ITEM_ORDER_DATA,
                    { id: this.collectionId, maxVariants: CollectionVariantOrderFormComponent.MAX_ITEMS },
                    'network-only'
                )
                .mapSingle((data: any) => data?.collection)
                .pipe(
                    map((collection: any) => ({
                        items: collection?.productVariants?.items ?? [],
                        markDirty,
                    })),
                );
            }
            return EMPTY;
        }

        // Skip preview when any non-array arg is still empty (not need to trigger invalid filter state)
        const hasUnconfiguredArgs = filtersControl.value.some((f: any) =>
            Object.values(f.args ?? {}).some(
                value => !Array.isArray(value) && (value ?? '').toString() === '',
            ),
        );
        if (hasUnconfiguredArgs) {
            return EMPTY;
        }

        const filtersInput = this.mapFiltersToInput(filtersControl.value);
        const inheritFilters = inheritFiltersControl?.value ?? true;

        return this.dataService.query(PREVIEW_COLLECTION_ITEM_ORDER_DATA,
            { input: { filters: filtersInput, inheritFilters }, maxVariants: CollectionVariantOrderFormComponent.MAX_ITEMS },
            'network-only'
        )
        .mapSingle((data: any) => data?.previewCollectionVariants?.items ?? [])
        .pipe(
            map((items: ProductVariant[]) => ({ items, markDirty })),
        );
    }

    private mapFiltersToInput(filters: any[]): any[] {
        return (filters ?? []).map((f: any) => ({
            code: f.code,
            // Use the same encoding as Vendure's encodeConfigArgValue
            arguments: Object.entries(f.args ?? {}).map(([name, value]) => ({
                name,
                value: Array.isArray(value) ? JSON.stringify(value) : (value ?? '').toString(),
            })),
        }));
    }

    private hydrateItemsFromCollection(collection: any) {
        this.hydrateItemsFromVariants(collection.productVariants?.items ?? []);
    }

    private hydrateItemsFromVariants(items: ProductVariant[]) {
        this.originalVariants = items;
        this.variantMap = new Map(items.map(v => [v.id, v]));
        this.originalProducts = Array.from(
            new Map(
                items.filter((v: any) => !!v.product).map((v: any) => [v.product.id, v.product])
            ).values()
        );
        this.productMap = new Map(this.originalProducts.map(p => [p.id, p]));
    }

    private bindControlValueChanges() {
        if (this.controlsBound) return;
        this.controlsBound = true;

        this.enabledControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.collectionItemOrderData?.enabled !== this.enabledControl.value) {
                    this.collectionItemOrderData!.enabled = this.enabledControl.value || false;
                    this.syncToDetailForm();
                }
            });

        this.orderTypeControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.collectionItemOrderData?.orderType !== this.orderTypeControl.value) {
                    this.collectionItemOrderData!.orderType = this.orderTypeControl.value as CollectionItemType;
                    this.syncOrderedItems();
                    this.syncToDetailForm();
                }
            });
    }

    private bindCollectionContentChanges() {
        const candidateControls = ['filters', 'inheritFilters']
            .map(name => ({ name, control: this.detailForm?.get(name) }))
            .filter((entry): entry is { name: string; control: AbstractControl } => !!entry.control);

        for (const { name, control } of candidateControls) {
            control.valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    startWith(control.value),
                    debounceTime(150),
                    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                    skip(1),
                )
                .subscribe(() => {
                    this.fetchPreviewCollectionData();
                });
        }
    }

    private syncOrderedItems() {
        if (this.orderTypeControl.value === CollectionItemType.ProductVariant) {
            this.updateOrderedVariants();
        } else {
            this.updateOrderedProducts();
        }
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Reconciles the displayed list after the item set changes (filter preview,
     * entity refresh, etc.)
     * - Items no longer in the collection are removed.
     * - New items (not yet in the ordered list) are appended at the end.
     * - Existing items keep their current display position.
     * The saved order in collectionItemOrderData is now only updated by:
     * - Initial load (from DB)
     * - drop() (user drag-drop)
     * - reconcileOrderedItems(markDirty=true) (user-initiated filter changes)
     */
    private reconcileOrderedItems(markDirty: boolean) {
         if (this.orderTypeControl.value === CollectionItemType.ProductVariant) {
            const variantByStrId = new Map(this.originalVariants.map(v => [String(v.id), v]));
            const currentIds = new Set(variantByStrId.keys());
            const savedOrder = this.collectionItemOrderData?.variantIdsInOrder ?? [];
            // Rebuild display list from saved order, keeping only items that currently exist
            this.orderedVariants = savedOrder
                .filter(id => currentIds.has(String(id)))
                .map(id => variantByStrId.get(String(id))!)
                .filter((v): v is ProductVariant => !!v);
            // Append genuinely new items not in saved order
            const savedIdSet = new Set(savedOrder.map(id => String(id)));
            const newVariants = this.originalVariants.filter(v => !savedIdSet.has(String(v.id)));
            this.orderedVariants.push(...newVariants);
            // Only update the saved order on user-initiated changes (markDirty=true).
            // On post-update refresh (markDirty=false), the DB may return a stale/partial
            // item set — updating the saved order here would corrupt the user's ordering.
            if (markDirty) {
                this.collectionItemOrderData!.variantIdsInOrder = this.orderedVariants.map(v => v.id);
            }
        } else {
            const productByStrId = new Map(this.originalProducts.map(p => [String(p.id), p]));
            const currentIds = new Set(productByStrId.keys());
            const savedOrder = this.collectionItemOrderData?.productIdsInOrder ?? [];

            this.orderedProducts = savedOrder
                .filter(id => currentIds.has(String(id)))
                .map(id => productByStrId.get(String(id))!)
                .filter((p): p is Product => !!p);

            const savedIdSet = new Set(savedOrder.map(id => String(id)));
            const newProducts = this.originalProducts.filter(p => !savedIdSet.has(String(p.id)));
            this.orderedProducts.push(...newProducts);

            if (markDirty) {
                this.collectionItemOrderData!.productIdsInOrder = this.orderedProducts.map(p => p.id);
            }
        }
        this.changeDetectorRef.markForCheck();
        if (markDirty) {
            this.syncToDetailForm();
        }
    }

    updateOrderedVariants() {
        this.orderedVariants = (this.collectionItemOrderData?.variantIdsInOrder ?? [])
            .map(id => this.variantMap?.get(id))
            .filter((v): v is ProductVariant => !!v);
        const unorderedVariants = this.originalVariants.filter(
            v => !this.collectionItemOrderData?.variantIdsInOrder.includes(v.id),
        );
        this.orderedVariants.push(...unorderedVariants);
        this.collectionItemOrderData!.variantIdsInOrder = this.orderedVariants.map(v => v.id);
    }

    updateOrderedProducts() {
        this.orderedProducts = (this.collectionItemOrderData?.productIdsInOrder ?? [])
            .map(id => this.productMap?.get(id))
            .filter((p): p is Product => !!p);
        const unorderedProducts = this.originalProducts.filter(
            p => !this.collectionItemOrderData?.productIdsInOrder.includes(p.id),
        );
        this.orderedProducts.push(...unorderedProducts);
        this.collectionItemOrderData!.productIdsInOrder = this.orderedProducts.map(p => p.id);
    }

    drop(event: CdkDragDrop<ID[]>) {
        if (this.orderTypeControl.value === CollectionItemType.ProductVariant) {
            moveItemInArray(this.orderedVariants, event.previousIndex, event.currentIndex);
            this.collectionItemOrderData!.variantIdsInOrder = this.orderedVariants.map(v => v.id);
        } else {
            moveItemInArray(this.orderedProducts, event.previousIndex, event.currentIndex);
            this.collectionItemOrderData!.productIdsInOrder = this.orderedProducts.map(p => p.id);
        }
        this.syncToDetailForm();
    }

    private syncToDetailForm() {
        const itemOrderData = JSON.stringify(this.collectionItemOrderData);
        const control = this.detailForm.get('customFields.itemOrderData');
        if (control) {
            control.setValue(itemOrderData);
            control.markAsDirty();
        }
    }
}
